import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Alert } from 'reactstrap';
import Select from 'react-select';
import { groupedAssetStatusOptions } from '../constants/utils';
import { useDowntimeReasons } from '../Hooks/useDowntimeReasons';
import usePermission from '../Hooks/usePermission';

// Pure helpers — exported for unit tests in non-React contexts.
export const todayIsoDate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// The HTML5 <input type="date"> only accepts ISO yyyy-MM-dd. The backend returns
// LocalDate fields as dd-MM-yyyy (see @JsonFormat on AssetResponse.decommissionedOn),
// so we have to normalize before binding it to the input or React silently renders it blank.
export const toIsoDate = (val) => {
  if (!val) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(val);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return '';
};

/**
 * Reusable component that renders the Asset Usage Status dropdown
 * and the conditional Downtime Reason dropdown (shown when OFFLINE is selected).
 *
 * Props:
 *   usageStatus        — current status value (e.g. 'ONLINE', 'OFFLINE', 'DO_NOT_TRACK')
 *   downtimeReasonId   — current downtime reason ID (number | null)
 *   onChange({ usageStatus, downtimeReasonId }) — callback with updated values
 *   errors             — { usageStatus?: string, downtimeReasonId?: string }
 *   variant            — 'react-select' | 'select' (default: 'select')
 *   layout             — 'stacked' | 'horizontal' | 'inline' (default: 'stacked')
 *   label              — custom label text (default: depends on layout)
 *   required           — whether to show required asterisk (default: false)
 *   disabled           — disable both fields (default: false)
 *   helperText         — optional helper text below status dropdown
 *   selectStyles       — custom styles for react-select variant (function or object)
 *   statusFieldName    — field name for errors key, default 'usageStatus'
 *   downtimeFieldName  — field name for errors key, default 'downtimeReasonId'
 *   id                 — optional id prefix for the fields
 *   showDecommissioned       — when true, expose DECOMMISSIONED in the dropdown (subject to role gate, default false)
 *   decommissionedReason     — current reason value when status is DECOMMISSIONED (default '')
 *   decommissionedOn         — current decommission date (ISO yyyy-MM-dd string, default '')
 *   currentEntityStatus      — the asset's CURRENT persisted status (for revive-only-by-admin gating)
 *   decommissionReasonFieldName  — errors key for the reason field (default 'decommissionedReason')
 */
const AssetUsageStatusFields = ({
  usageStatus = '',
  downtimeReasonId = null,
  onChange,
  errors = {},
  variant = 'select',
  layout = 'stacked',
  label,
  required = false,
  disabled = false,
  helperText,
  selectStyles: customSelectStyles,
  statusFieldName = 'usageStatus',
  downtimeFieldName = 'downtimeReasonId',
  id = '',
  showDecommissioned = false,
  decommissionedReason = '',
  decommissionedOn = '',
  currentEntityStatus = null,
  decommissionReasonFieldName = 'decommissionedReason',
}) => {
  const { t } = useTranslation();
  const { downtimeReasons, loadingReasons } = useDowntimeReasons();
  const { isAdmin } = usePermission();

  const statusError = errors[statusFieldName];
  const downtimeError = errors[downtimeFieldName];
  const decommissionReasonError = errors[decommissionReasonFieldName];

  // The DECOMMISSIONED option is only ever surfaced if the caller opted in AND the user
  // has admin/super-admin privileges. Non-admins must not be able to retire an asset.
  const canPickDecommissioned = showDecommissioned && isAdmin;
  // Reviving (transitioning AWAY from DECOMMISSIONED) is also admin-only.
  const isReviveAttempt = currentEntityStatus === 'DECOMMISSIONED'
    && usageStatus
    && usageStatus !== 'DECOMMISSIONED';
  const reviveBlocked = isReviveAttempt && !isAdmin;

  const handleStatusChange = (newStatus) => {
    if (newStatus === 'DECOMMISSIONED') {
      onChange({
        usageStatus: newStatus,
        downtimeReasonId: null,
        decommissionedReason: decommissionedReason || '',
        decommissionedOn: decommissionedOn || todayIsoDate(),
      });
    } else {
      onChange({
        usageStatus: newStatus,
        downtimeReasonId: null,
        decommissionedReason: '',
        decommissionedOn: '',
      });
    }
  };

  const handleDowntimeReasonChange = (newReasonId) => {
    onChange({ usageStatus, downtimeReasonId: newReasonId, decommissionedReason, decommissionedOn });
  };

  const handleDecommissionedReasonChange = (val) => {
    onChange({ usageStatus, downtimeReasonId, decommissionedReason: val, decommissionedOn });
  };

  const handleDecommissionedOnChange = (val) => {
    onChange({ usageStatus, downtimeReasonId, decommissionedReason, decommissionedOn: val });
  };

  // Hide DECOMMISSIONED group unless the caller opted in AND the user is admin.
  const visibleStatusGroups = groupedAssetStatusOptions(t).filter(
    (g) => g.options.every((opt) => opt.value !== 'DECOMMISSIONED') || canPickDecommissioned
  );

  const statusLabel = label || (layout === 'horizontal' ? t('shared.usage_status', 'Usage Status') : t('shared.asset_usage_status', 'Asset Usage Status'));
  const statusId = id ? `${id}UsageStatus` : 'usageStatus';
  const downtimeId = id ? `${id}DowntimeReasonId` : 'downtimeReasonId';

  // --- Downtime Reason dropdown (shared across all variants/layouts) ---
  const downtimeReasonSelect = (
    <>
      <select
        id={downtimeId}
        className={`form-select ${downtimeError ? 'is-invalid' : ''}`}
        value={downtimeReasonId || ''}
        onChange={(e) => handleDowntimeReasonChange(e.target.value ? Number(e.target.value) : null)}
        disabled={disabled || loadingReasons}
      >
        <option value="">{loadingReasons ? t('shared.loading', 'Loading...') : t('shared.select_downtime_reason', 'Select downtime reason')}</option>
        {downtimeReasons.map((reason) => (
          <option key={reason.id} value={reason.id}>
            {t(`shared.${reason.label.toLowerCase().replace(/\s+/g, '_')}`, reason.label)}{reason.description ? ` — ${t(`shared.downtime_${reason.label.toLowerCase().replace(/\s+/g, '_')}_desc`, reason.description)}` : ''}
          </option>
        ))}
      </select>
      {downtimeError && <small className="text-danger">{downtimeError}</small>}
    </>
  );

  // --- Native <select> status dropdown ---
  const nativeStatusSelect = (
    <>
      <select
        id={statusId}
        className={`form-select ${statusError ? 'is-invalid' : ''}`}
        value={usageStatus || ''}
        onChange={(e) => handleStatusChange(e.target.value)}
        disabled={disabled || reviveBlocked}
      >
        <option value="">{t('shared.select_usage_status', 'Select Usage Status')}</option>
        {visibleStatusGroups.map((group) => (
          <optgroup key={group.group} label={group.group}>
            {group.options.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}{status.hint ? ` — ${status.hint}` : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {statusError && <small className="text-danger">{statusError}</small>}
    </>
  );

  // --- react-select status dropdown ---
  const richStatusSelect = (
    <>
      <Select
        id={statusId}
        options={visibleStatusGroups.flatMap((group) =>
          group.options.map((status) => ({
            value: status.value,
            label: status.label,
            color: status.color,
            hint: status.hint,
          }))
        )}
        value={usageStatus ? (() => {
          for (const group of visibleStatusGroups) {
            const found = group.options.find(opt => opt.value === usageStatus);
            if (found) return { value: found.value, label: found.label, color: found.color, hint: found.hint };
          }
          return null;
        })() : null}
        onChange={(selected) => handleStatusChange(selected?.value || '')}
        formatOptionLabel={(opt, { context }) => (
          <div>
            <div className="d-flex align-items-center gap-2">
              <span className="rounded-circle flex-shrink-0" style={{ width: 8, height: 8, backgroundColor: opt.color || '#6b7280' }} />
              <span>{opt.label}</span>
            </div>
            {opt.hint && context === 'menu' && <small className="d-block ms-3" style={{ fontSize: '0.75rem', opacity: 0.7 }}>{opt.hint}</small>}
          </div>
        )}
        isClearable
        isSearchable
        isDisabled={disabled || reviveBlocked}
        placeholder={t('shared.select_asset_usage_status', 'Select Asset Usage Status')}
        classNamePrefix="react-select"
        styles={typeof customSelectStyles === 'function' ? customSelectStyles(!!statusError) : (customSelectStyles || defaultReactSelectStyles(!!statusError))}
      />
      {statusError && <small className="text-danger">{statusError}</small>}
    </>
  );

  const statusSelect = variant === 'react-select' ? richStatusSelect : nativeStatusSelect;
  const isOffline = usageStatus === 'OFFLINE';
  const isDecommissioned = usageStatus === 'DECOMMISSIONED';
  const decommissionedReasonId = id ? `${id}DecommissionedReason` : 'decommissionedReason';
  const decommissionedOnId = id ? `${id}DecommissionedOn` : 'decommissionedOn';

  // Block shown above the field when a non-admin opens the form for an already-decommissioned asset.
  const reviveBlockedNotice = reviveBlocked ? (
    <Alert color="warning" className="mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.875rem' }}>
      {t('business.assets.errors.revive_admin_only', 'Only an Administrator or Super Admin can revive a decommissioned asset.')}
    </Alert>
  ) : null;

  // Date input only accepts ISO yyyy-MM-dd. Backend may hand us dd-MM-yyyy; coerce here.
  const decommissionedOnIso = toIsoDate(decommissionedOn) || todayIsoDate();

  const decommissionAlert = isDecommissioned ? (
    <Alert color="warning" className="mb-0 py-2 px-3" style={{ fontSize: '0.875rem' }}>
      {t('shared.decommission_warning_hidden_from_list', 'This asset will be hidden from the standard asset list. Only Administrators can revive it.')}
    </Alert>
  ) : null;

  const decommissionReasonField = isDecommissioned ? (
    <div className="form-group">
      <label htmlFor={decommissionedReasonId} className="form-label fw-medium">
        {t('business.assets.decommission_reason', 'Decommission reason')} <span className="text-danger">*</span>
      </label>
      <textarea
        id={decommissionedReasonId}
        className={`form-control ${decommissionReasonError ? 'is-invalid' : ''}`}
        rows={3}
        maxLength={500}
        value={decommissionedReason || ''}
        onChange={(e) => handleDecommissionedReasonChange(e.target.value)}
        disabled={disabled}
        placeholder={t('business.assets.decommission_reason_helper', 'Briefly describe why this asset is being decommissioned (e.g. sold, scrapped, replaced).')}
      />
      {decommissionReasonError && <small className="text-danger">{decommissionReasonError}</small>}
    </div>
  ) : null;

  const decommissionDateField = isDecommissioned ? (
    <div className="form-group">
      <label htmlFor={decommissionedOnId} className="form-label fw-medium">
        {t('business.assets.decommissioned_on', 'Decommissioned on')}
      </label>
      <input
        id={decommissionedOnId}
        type="date"
        className="form-control"
        value={decommissionedOnIso}
        onChange={(e) => handleDecommissionedOnChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  ) : null;

  // --- Layout: stacked (full-width rows) ---
  if (layout === 'stacked') {
    return (
      <>
        <Row className="mb-4">
          <Col md={12}>
            <div className="form-group">
              <label htmlFor={statusId} className="form-label fw-medium">
                {statusLabel}{required && <> <span className="text-danger">*</span></>}
              </label>
              {statusSelect}
              {helperText && <small className="text-muted d-block mt-1">{helperText}</small>}
              {reviveBlockedNotice}
            </div>
          </Col>
        </Row>
        {isOffline && (
          <Row className="mb-4">
            <Col md={12}>
              <div className="form-group">
                <label htmlFor={downtimeId} className="form-label fw-medium">
                  {t('shared.downtime_reason', 'Downtime Reason')} <span className="text-danger">*</span>
                </label>
                {downtimeReasonSelect}
              </div>
            </Col>
          </Row>
        )}
        {isDecommissioned && (
          <>
            <Row className="mb-3">
              <Col md={12}>{decommissionAlert}</Col>
            </Row>
            <Row className="mb-4">
              <Col md={12}>{decommissionReasonField}</Col>
            </Row>
            <Row className="mb-4">
              <Col md={12}>{decommissionDateField}</Col>
            </Row>
          </>
        )}
      </>
    );
  }

  // --- Layout: horizontal (label col-sm-3 + field col-sm-9) ---
  if (layout === 'horizontal') {
    return (
      <>
        <Row className="mb-4">
          <label htmlFor={statusId} className="col-sm-3 col-form-label">
            {statusLabel}{required && <> <span className="text-danger">*</span></>}
          </label>
          <Col sm={9}>
            {statusSelect}
            {helperText && <small className="text-muted d-block mt-1">{helperText}</small>}
            {reviveBlockedNotice}
          </Col>
        </Row>
        {isOffline && (
          <Row className="mb-4">
            <label htmlFor={downtimeId} className="col-sm-3 col-form-label">
              {t('shared.downtime_reason', 'Downtime Reason')} <span className="text-danger">*</span>
            </label>
            <Col sm={9}>
              {downtimeReasonSelect}
            </Col>
          </Row>
        )}
        {isDecommissioned && (
          <>
            <Row className="mb-3">
              <Col sm={{ size: 9, offset: 3 }}>{decommissionAlert}</Col>
            </Row>
            <Row className="mb-4">
              <Col sm={{ size: 9, offset: 3 }}>{decommissionReasonField}</Col>
            </Row>
            <Row className="mb-4">
              <Col sm={{ size: 9, offset: 3 }}>{decommissionDateField}</Col>
            </Row>
          </>
        )}
      </>
    );
  }

  // --- Layout: inline (Col md={6} each, no wrapping Row) ---
  if (layout === 'inline') {
    return (
      <>
        <Col md={6}>
          <label htmlFor={statusId} className="form-label">
            {statusLabel}{required && <> <span className="text-danger">*</span></>}
          </label>
          {statusSelect}
          {reviveBlockedNotice}
        </Col>
        {isOffline && (
          <Col md={6}>
            <label htmlFor={downtimeId} className="form-label">
              {t('shared.downtime_reason', 'Downtime Reason')} <span className="text-danger">*</span>
            </label>
            {downtimeReasonSelect}
          </Col>
        )}
        {isDecommissioned && (
          <>
            <Col md={12}>{decommissionAlert}</Col>
            <Col md={12}>{decommissionReasonField}</Col>
            <Col md={12}>{decommissionDateField}</Col>
          </>
        )}
      </>
    );
  }

  return null;
};

// Default styles for react-select variant (matches existing WorkOrderForm pattern)
const defaultReactSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    borderColor: hasError ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#ced4da'),
    boxShadow: hasError
      ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
      : (state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none'),
    '&:hover': { borderColor: '#86b7fe' }
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#3980c0' : 'white',
    color: state.isSelected || state.isFocused ? 'white' : '#212529',
    '&:active': { backgroundColor: '#2e669a' }
  })
});

export default AssetUsageStatusFields;
